/**
 * Veridex Brand Logo
 * A modern, minimal enterprise SaaS mark: geometric "V" with a green leaf
 * growing organically from its right arm — symbolizing verification +
 * sustainability. Built as clean SVG, scales crisp from 16px (favicon) up to
 * hero-size and works on both light and dark surfaces.
 *
 * Props:
 *  - size:     number (pixel height for icon mark), default 32
 *  - variant:  'full' | 'icon'  (show wordmark beside the mark or mark only)
 *  - tone:     'auto' | 'light' | 'dark' | 'mono-black' | 'mono-white'
 *              (wordmark + mark rendering for invoices / print)
 *  - tagline:  optional boolean — renders the brand tagline under the wordmark
 *  - beta:     boolean, default true — renders a small "Beta" pill next to the
 *              wordmark. Set to false for print / invoices / legal docs.
 *  - className / other props forwarded to the wrapper span
 */
export default function Logo({ size = 32, variant = 'full', tone = 'auto', tagline = false, beta = true, className = '', ...rest }) {
  const h = size;
  const mono = tone === 'mono-black' || tone === 'mono-white';
  const monoColor = tone === 'mono-white' ? '#FFFFFF' : '#0F172A';

  const textClass =
    tone === 'light'
      ? 'text-white'
      : tone === 'dark'
      ? 'text-slate-900'
      : mono
      ? ''
      : 'text-foreground';

  const mark = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={h}
      height={h}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label="Veridex One"
      className="shrink-0"
      data-testid="veridex-logo-mark"
    >
      <defs>
        <linearGradient id="vx-g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1E3A8A" />
          <stop offset="100%" stopColor="#0F172A" />
        </linearGradient>
        <linearGradient id="vx-leaf" x1="34" y1="8" x2="58" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#22C55E" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>

      {/* Rounded square tile */}
      <rect x="0" y="0" width="64" height="64" rx="14" fill={mono ? monoColor : 'url(#vx-g)'} />

      {/* Left arm of the V */}
      <path
        d="M14 17 L28 46 L32 46 L22 17 Z"
        fill={mono ? (tone === 'mono-white' ? '#0F172A' : '#FFFFFF') : '#FFFFFF'}
        opacity={mono ? '1' : '0.96'}
      />

      {/* Right arm of the V morphing into the leaf spine */}
      <path
        d="M42 17 L32 46 L28 46 L38 17 Z"
        fill={mono ? (tone === 'mono-white' ? '#0F172A' : '#FFFFFF') : '#FFFFFF'}
        opacity={mono ? '0.45' : '0.55'}
      />

      {/* The leaf — organically grows from the right arm's top */}
      <path
        d="M42 17
           C 50 16, 56 22, 55 32
           C 54 40, 47 45, 38 44
           C 41 36, 42 26, 42 17 Z"
        fill={mono ? (tone === 'mono-white' ? '#0F172A' : '#FFFFFF') : 'url(#vx-leaf)'}
        opacity={mono ? '0.85' : '1'}
      />

      {/* Leaf midrib */}
      <path
        d="M42 18 C 46 24, 48 32, 44 41"
        stroke={mono ? (tone === 'mono-white' ? '#FFFFFF' : '#0F172A') : '#FFFFFF'}
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.55"
        fill="none"
      />
    </svg>
  );

  if (variant === 'icon') {
    return (
      <span className={`inline-flex items-center ${className}`} {...rest}>
        {mark}
      </span>
    );
  }

  const wordmarkStyle = mono ? { color: monoColor, fontSize: Math.max(14, Math.round(h * 0.58)), letterSpacing: '-0.02em' } : { fontSize: Math.max(14, Math.round(h * 0.58)), letterSpacing: '-0.02em' };

  return (
    <span
      className={`inline-flex items-center gap-2.5 ${className}`}
      data-testid="veridex-logo"
      {...rest}
    >
      {mark}
      <span className="flex flex-col leading-none">
        <span className="flex items-center gap-1.5">
          <span
            className={`font-heading font-semibold tracking-tight ${textClass}`}
            style={wordmarkStyle}
          >
            Veridex One
          </span>
          {beta && !mono && (
            <span
              className="uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-300/70 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800/60"
              style={{ fontSize: Math.max(8, Math.round(h * 0.28)), lineHeight: 1 }}
              data-testid="veridex-beta-badge"
            >
              Beta
            </span>
          )}
        </span>
        {tagline && (
          <span className="mt-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium" data-testid="veridex-tagline">
            Reduce CBAM Risk · Increase Audit Confidence
          </span>
        )}
      </span>
    </span>
  );
}
